'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/comps/primitives/skeleton';
import { AI_SPEND_KPI_GRID_CLASS, AVG_SPEND_PER_TASK_CARD_TOOLTIP, AVG_SPEND_PER_TASK_VISUAL_BLOCK_HEIGHT_PX, SPEND_BY_MODEL_CARD_TITLE, SPEND_BY_MODEL_CARD_TOOLTIP, SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS, SPEND_DISTRIBUTION_CARD_TITLE, SPEND_DISTRIBUTION_CARD_TOOLTIP, SPEND_DISTRIBUTION_SECTION_LOADING_HEIGHT_PX, SPEND_KPI_VALUE_ROW_HEIGHT_PX, SPEND_OVER_TIME_CARD_TITLE, SPEND_OVER_TIME_CARD_TOOLTIP, SPEND_OVER_TIME_CHART_MIN_HEIGHT_CLASS, TOTAL_AI_SPEND_CARD_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import { pageKeyToSpendSegmentKind } from '@/domains/pages/ai-spend/utils/ai-spend-view.utils';
import { SegmentScaleChartSkeleton } from '@/domains/pages/shared/segment-scale-chart';
import { segmentKindLabel } from '@/domains/pages/shared/segment-kind';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import type { NebulyPageKey } from '@/domains/pages/types';
import { capitalizeFirstLetter } from '@/utils/capitalize-first-letter';
import { cn } from '@/utils/shad';
const KPI_VALUE_SKELETON_CLASS = 'w-30';
function SegmentScaleSectionSkeleton(t0) {
  const $ = _c(7);
  const {
    pageKey
  } = t0;
  let T0;
  let t1;
  if ($[0] !== pageKey) {
    const kind = pageKeyToSpendSegmentKind(pageKey);
    const noun = kind ? segmentKindLabel(kind) : "segment";
    T0 = NebulyPageCard;
    t1 = capitalizeFirstLetter(noun);
    $[0] = pageKey;
    $[1] = T0;
    $[2] = t1;
  } else {
    T0 = $[1];
    t1 = $[2];
  }
  const t2 = `${t1} avg. AI spend per task`;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SegmentScaleChartSkeleton testId="segment-spend-scale-card-skeleton" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== T0 || $[5] !== t2) {
    t4 = <T0 title={t2} className="min-h-0 border-b border-b-lightborder">{t3}</T0>;
    $[4] = T0;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function SegmentPickerBarSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-w-0 flex-nowrap items-center gap-2 border-b border-b-lightborder px-8 py-2"><Skeleton className="h-7 w-52 max-w-full rounded-md" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function KpiStackSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NebulyPageCard title="Total AI spend" description={TOTAL_AI_SPEND_CARD_TOOLTIP} className="grow-0 border-b border-b-lightborder" contentClassName="flex flex-col gap-3 !py-3"><Skeleton className={KPI_VALUE_SKELETON_CLASS} style={{
        height: SPEND_KPI_VALUE_ROW_HEIGHT_PX
      }} /></NebulyPageCard>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex min-w-0 flex-col border-r border-r-lightborder">{t0}<NebulyPageCard title="Avg. AI spend per task" description={AVG_SPEND_PER_TASK_CARD_TOOLTIP} className="grow-0" contentClassName="flex flex-col gap-3 !py-3"><Skeleton className={KPI_VALUE_SKELETON_CLASS} style={{
          height: SPEND_KPI_VALUE_ROW_HEIGHT_PX
        }} /><div style={{
          height: AVG_SPEND_PER_TASK_VISUAL_BLOCK_HEIGHT_PX
        }} /></NebulyPageCard></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function OverTimeChartSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-full min-w-0"><NebulyPageCard title={SPEND_OVER_TIME_CARD_TITLE} description={SPEND_OVER_TIME_CARD_TOOLTIP} fillHeight={true} className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col"><div className={cn("w-full flex-1", SPEND_OVER_TIME_CHART_MIN_HEIGHT_CLASS)} /></NebulyPageCard></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function TaskModelSectionSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NebulyPageCard title={SPEND_BY_MODEL_CARD_TITLE} description={SPEND_BY_MODEL_CARD_TOOLTIP} className="min-h-0" contentClassName="flex flex-col gap-4 !py-4"><div className={cn("w-full", SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS)} /></NebulyPageCard>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function DistributionSectionSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NebulyPageCard title={SPEND_DISTRIBUTION_CARD_TITLE} description={SPEND_DISTRIBUTION_CARD_TOOLTIP} className="min-h-0" contentClassName="flex flex-col gap-4 !py-4"><div className="w-full" style={{
        height: SPEND_DISTRIBUTION_SECTION_LOADING_HEIGHT_PX
      }} /></NebulyPageCard>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function createAiSpendSegmentPageLoadingSkeleton(pageKey: NebulyPageKey) {
  return function AiSpendSegmentPageLoadingSkeleton() {
    return <div className="border-b border-b-lightborder" data-testid="nebuly-page-loading">
        <SegmentPickerBarSkeleton />
        <SegmentScaleSectionSkeleton pageKey={pageKey} />

        <div className={cn(AI_SPEND_KPI_GRID_CLASS, 'items-stretch border-b border-b-lightborder')}>
          <KpiStackSkeleton />
          <OverTimeChartSkeleton />
        </div>

        <DistributionSectionSkeleton />
        <TaskModelSectionSkeleton />
      </div>;
  };
}